/**
 * Notifications, the browser side (docs/notifications/contract.md; docs/NESTS.md, "Stage 3:
 * notifications"): can this browser take them, turning them on from a tap, the switches
 * and times, the test, the play state, turning them off, keeping the subscription alive
 * on launch, and the moments' memory. Nothing here throws into the UI.
 *
 * The OS prompt only ever comes from a tap: `turnOn` calls `pushManager.subscribe`
 * straight from the handler (WebKit's rule), with the VAPID key fetched beforehand, so
 * there's no network call in between.
 */
import { localDateKey } from '@arrow/game-engine';
import { useEffect, useState, useSyncExternalStore } from 'react';

import { type NotifySurface, track } from '../platform/analytics.ts';
import { platformStore } from '../platform/config.ts';
import { type PlatformInfo, readPlatform } from '../pwa/platform.ts';
import type { KeyValueStore } from '../storage/kv.ts';
import { fetchPushConfig, PushApiError, type PushConfig, postPreferences, postSubscription, postTest, postUnsubscribe } from './api.ts';
import {
	afterNotNow,
	anySwitchOn,
	DEFAULT_PREFS,
	loadPushState,
	type MomentKind,
	type MomentMemory,
	NO_PREFS,
	noteShown,
	type NotifyState,
	notifyState,
	type Permission,
	type Played,
	playedKey,
	type PushPrefs,
	type PushRoute,
	type PushState,
	PUSH_KEY,
	savePushState,
	type SwitchKey,
	type TimeKey,
} from './state.ts';

const EVENT = 'arrow:push';
let store: KeyValueStore = platformStore;

/** Tests: run over another store. */
export function setPushStore(next: KeyValueStore): void {
	store = next;
	cached = null;
}

let cached: { raw: string | null; value: PushState } | null = null;

export function pushState(): PushState {
	const raw = store.get(PUSH_KEY);
	if (cached && cached.raw === raw) return cached.value;
	cached = { raw, value: loadPushState(store) };
	return cached.value;
}

function save(next: PushState): void {
	savePushState(store, next);
	if (typeof window !== 'undefined') window.dispatchEvent(new Event(EVENT));
}

function update(fn: (s: PushState) => PushState): PushState {
	const next = fn(pushState());
	save(next);
	return next;
}

export function usePushState(): PushState {
	return useSyncExternalStore(
		(fn) => {
			const onStorage = (e: StorageEvent) => {
				if (e.key === null || e.key === PUSH_KEY) fn();
			};
			window.addEventListener(EVENT, fn);
			window.addEventListener('storage', onStorage);
			return () => {
				window.removeEventListener(EVENT, fn);
				window.removeEventListener('storage', onStorage);
			};
		},
		pushState,
		pushState,
	);
}

// --------------------------------------------------------------------------- the browser

interface PushWindow {
	PushManager?: unknown;
	Notification?: { permission?: string };
}

/** Whether this browser has what web push needs (a service worker, PushManager, Notification). */
export function hasPushApis(win: PushWindow | undefined = typeof window === 'undefined' ? undefined : (window as PushWindow)): boolean {
	try {
		return !!win && 'PushManager' in win && 'Notification' in win && typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
	} catch {
		return false;
	}
}

/**
 * Where this browser stands. An iPhone or iPad in a browser tab can't take web push at
 * all: it needs Sparrow on the home screen first (the install guide). An app's own browser
 * can do neither.
 */
export function pushRoute(platform: PlatformInfo, apis: boolean = hasPushApis()): PushRoute {
	if (platform.inApp) return 'none';
	if (apis) return 'push';
	if (platform.ios && !platform.standalone) return 'install';
	return 'none';
}

export function permission(): Permission {
	try {
		const p = (window as PushWindow).Notification?.permission;
		return p === 'granted' || p === 'denied' ? p : 'default';
	} catch {
		return 'default';
	}
}

/** `Notification.permission`, re-read when the page comes back (it may have changed in Settings). */
export function usePermission(): Permission {
	const [p, setP] = useState(permission);
	useEffect(() => {
		const again = () => setP(permission());
		document.addEventListener('visibilitychange', again);
		return () => document.removeEventListener('visibilitychange', again);
	}, []);
	return p;
}

/** The api's answer to "are notifications on?", once per page load. */
export function usePushConfig(): PushConfig | null {
	const [config, setConfig] = useState<PushConfig | null>(null);
	useEffect(() => {
		let live = true;
		void fetchPushConfig().then((c) => live && setConfig(c));
		return () => {
			live = false;
		};
	}, []);
	return config;
}

export const timeZone = (): string => {
	try {
		return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
	} catch {
		return 'UTC';
	}
};

/** The service worker's registration, or null if there isn't one within `ms` (dev, or it failed). */
async function registration(ms = 4000): Promise<ServiceWorkerRegistration | null> {
	try {
		if (!('serviceWorker' in navigator)) return null;
		return await Promise.race([navigator.serviceWorker.ready, new Promise<null>((r) => setTimeout(() => r(null), ms))]);
	} catch {
		return null;
	}
}

/** The VAPID public key (base64url) as the bytes `subscribe` wants. */
export function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
	const b64 = base64url.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (base64url.length % 4)) % 4);
	const bin = atob(b64);
	const out = new Uint8Array(new ArrayBuffer(bin.length));
	for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
	return out;
}

// ------------------------------------------------------------------------- the play state

/** The app's play state (the last daily finished on its day, and the streak): set once by the app. */
let playedSource: () => Played | null = () => null;

export function setPlayedSource(fn: () => Played | null): void {
	playedSource = fn;
}

const played = (): Played | null => {
	try {
		return playedSource();
	} catch {
		return null;
	}
};

/** The play state now (the sheet's examples use the streak). */
export const currentPlayed = (): Played | null => played();

// ------------------------------------------------------------------------- turning on/off

/**
 * `unavailable`: this browser can't take a push subscription (no service worker, no push
 * service, as Brave with Google's push off, or notifications off on the api). `failed`:
 * it could, but the api didn't answer (usually the connection).
 */
export type TurnOnResult = 'on' | 'denied' | 'dismissed' | 'unavailable' | 'failed';
const SUBSCRIBE_TIMEOUT_MS = 30_000;

/**
 * From a tap, and only from one (the sheet's "Turn on notifications", Turn back on):
 * subscribe (which shows the OS prompt the first time), then tell the api. Not on yet:
 * `prefs` over the contract's defaults. Already on: `prefs` over what's on.
 * `surface`: where the prompt came from, for `notify_prompt_result`.
 */
export async function turnOn(prefs: Partial<PushPrefs>, surface?: NotifySurface): Promise<TurnOnResult> {
	const config = await fetchPushConfig();
	if (!config.enabled || !config.publicKey) return 'unavailable';
	const reg = await registration();
	if (!reg) return 'unavailable';
	const asked = permission() === 'default';
	let sub: PushSubscription | null;
	try {
		// A browser's first subscription can take a few seconds (it registers with its push
		// service); one that never answers is a failure, not a switch stuck half-way.
		sub = await Promise.race([
			reg.pushManager.getSubscription().then((s) => s ?? reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(config.publicKey!) })),
			new Promise<null>((r) => setTimeout(() => r(null), SUBSCRIBE_TIMEOUT_MS)),
		]);
	} catch {
		const p = permission();
		const result = p === 'denied' ? 'denied' : p === 'default' ? 'dismissed' : 'unavailable';
		if (surface && asked && result !== 'unavailable') track('notify_prompt_result', { surface, result });
		return result;
	}
	if (surface && asked && sub) track('notify_prompt_result', { surface, result: 'granted' });
	// The push service never answered.
	if (!sub) return 'unavailable';
	const s = pushState();
	const next = s.on ? { ...s.prefs, ...prefs } : { ...DEFAULT_PREFS, ...prefs };
	try {
		const zone = timeZone();
		const p = played();
		const status = await postSubscription(sub, zone, next, p);
		update((st) => ({ ...st, on: true, stopped: false, prefs: status.prefs, zone, syncedOn: localDateKey(new Date()), playedSent: playedKey(p) }));
		return 'on';
	} catch {
		return 'failed';
	}
}

/** Notifications off on this device: the api deletes the address, and the browser drops it. */
export async function turnOff({ forget = false }: { forget?: boolean } = {}): Promise<boolean> {
	let ok = true;
	// Called before the request goes, so it carries this device's id even if storage is cleared next.
	const unsubscribed = postUnsubscribe();
	try {
		await unsubscribed;
	} catch {
		ok = false;
	}
	try {
		const reg = await registration(1500);
		await (await reg?.pushManager.getSubscription())?.unsubscribe();
	} catch {
		// The api forgot it either way; a stale browser subscription gets 404s and nothing else.
	}
	// `forget`: the device's data is being cleared; leave nothing behind.
	if (!forget) update((s) => ({ ...s, on: false, stopped: false, prefs: { ...NO_PREFS }, playedSent: null }));
	return ok;
}

/** The api lost the subscription (404): stopped here, so the bell and the sheet offer Turn back on. */
function lost(): void {
	update((s) => (s.on ? { ...s, stopped: true } : s));
}

/**
 * One switch, once on. The last one off: notifications off altogether (the address is
 * deleted). Not on yet: subscribe from this tap with it on.
 */
export async function setPref(key: SwitchKey, value: boolean): Promise<TurnOnResult | 'off' | 'saved'> {
	const s = pushState();
	if (!s.on || s.stopped) return value ? turnOn({ [key]: true }, 'settings') : 'saved';
	const next = { ...s.prefs, [key]: value };
	track('notify_pref_changed', { key, on: value });
	if (!anySwitchOn(next)) return (await turnOff()) ? 'off' : 'failed';
	update((st) => ({ ...st, prefs: next }));
	try {
		const status = await postPreferences({ prefs: { [key]: value } });
		update((st) => ({ ...st, prefs: status.prefs }));
		return 'saved';
	} catch (error) {
		if (error instanceof PushApiError && error.status === 404) {
			lost();
			return 'failed';
		}
		update((st) => ({ ...st, prefs: s.prefs }));
		return 'failed';
	}
}

/** One time (the morning reminder's, the streak saver's, quiet hours), once on. */
export async function setTime(key: TimeKey, value: string): Promise<'saved' | 'failed'> {
	const s = pushState();
	update((st) => ({ ...st, prefs: { ...st.prefs, [key]: value } }));
	if (!s.on || s.stopped) return 'saved';
	track('notify_pref_changed', { key });
	try {
		const status = await postPreferences({ prefs: { [key]: value } });
		update((st) => ({ ...st, prefs: status.prefs }));
		return 'saved';
	} catch (error) {
		if (error instanceof PushApiError && error.status === 404) lost();
		else update((st) => ({ ...st, prefs: { ...st.prefs, [key]: s.prefs[key] } }));
		return 'failed';
	}
}

/** "Send a test notification": `sent`; `gone` (the api has no subscription now: stopped); `limited` (3 an hour); `failed`. */
export type TestResult = 'sent' | 'gone' | 'limited' | 'failed';

export async function sendTest(): Promise<TestResult> {
	try {
		await postTest();
		track('notify_test_sent', {});
		return 'sent';
	} catch (error) {
		const status = error instanceof PushApiError ? error.status : 0;
		if (status === 404) {
			lost();
			return 'gone';
		}
		return status === 429 ? 'limited' : 'failed';
	}
}

/** What the test button's line says after a press. */
export function testLine(result: TestResult): string {
	if (result === 'sent') return 'Sent. It should arrive in a moment.';
	if (result === 'limited') return 'That’s three tests this hour. Try again later.';
	if (result === 'gone') return 'Notifications stopped on this device. Turn them back on to test.';
	return 'Couldn’t send it. Try again.';
}

/**
 * A daily finished (today's, on its day): while subscribed, tell the api the day and the
 * streak, so tonight's streak saver and tomorrow's reminder know. Never throws.
 */
export async function notePlayed(): Promise<void> {
	try {
		const s = pushState();
		if (!s.on || s.stopped) return;
		const p = played();
		const key = playedKey(p);
		if (!p || key === s.playedSent) return;
		await postPreferences({ played: p });
		update((st) => ({ ...st, playedSent: key }));
	} catch (error) {
		if (error instanceof PushApiError && error.status === 404) lost();
	}
}

/**
 * On launch: if notifications are on here, check the browser still has the subscription
 * (it can vanish after a phone update or a reinstall) and, once a day or when the time zone
 * changes, send it again: the api then has the current address, zone and choices. The play
 * state goes with it, or on its own when it changed since it was last sent.
 */
export async function syncPush(now: Date = new Date()): Promise<void> {
	try {
		const s = pushState();
		if (!s.on) return;
		const config = await fetchPushConfig();
		if (!config.enabled) return;
		const reg = await registration();
		if (!reg) return;
		const sub = await reg.pushManager.getSubscription();
		if (!sub) {
			const p = permission();
			update((st) => (p === 'denied' ? { ...st, on: false, stopped: false, prefs: { ...NO_PREFS } } : { ...st, stopped: true }));
			return;
		}
		const zone = timeZone();
		const today = localDateKey(now);
		const p = played();
		const key = playedKey(p);
		if (s.syncedOn === today && s.zone === zone && !s.stopped) {
			if (p && key !== s.playedSent) {
				await postPreferences({ played: p });
				update((st) => ({ ...st, playedSent: key }));
			}
			return;
		}
		const status = await postSubscription(sub, zone, s.prefs, p);
		update((st) => ({ ...st, prefs: status.prefs, zone, syncedOn: today, stopped: false, playedSent: key }));
	} catch (error) {
		if (error instanceof PushApiError && error.status === 404) lost();
		// Otherwise: next launch.
	}
}

// ------------------------------------------------------------------------ the one state

export interface NotifyStatus {
	/** Null until the api has answered, or while it has notifications off: show nothing. */
	state: NotifyState | null;
	route: PushRoute;
	permission: Permission;
	platform: PlatformInfo;
	push: PushState;
}

/** The bell's, the sheet's and the moments' one view of notifications here. */
export function useNotifyStatus(): NotifyStatus {
	const push = usePushState();
	const config = usePushConfig();
	const perm = usePermission();
	const [platform] = useState(readPlatform);
	const route = pushRoute(platform);
	const state = config?.enabled ? notifyState({ route, permission: perm, on: push.on, stopped: push.stopped }) : null;
	return { state, route, permission: perm, platform, push };
}

/** `notify_state`, once a local day. */
export function noteStateSeen(state: NotifyState, today = localDateKey(new Date())): void {
	if (pushState().stateSentOn === today) return;
	update((s) => ({ ...s, stateSentOn: today }));
	track('notify_state', { state });
}

// ---------------------------------------------------------------------------- the moments

export function noteMomentShown(kind: MomentKind, today = localDateKey(new Date())): void {
	const m = pushState().moments;
	const next = noteShown(m, kind, today);
	if (next === m) return;
	update((s) => ({ ...s, moments: next }));
}

/** "Not now" on any moment: they all rest (7 days, then 30, then for good). */
export function noteNotNow(today = localDateKey(new Date())): MomentMemory {
	const next = { ...afterNotNow(pushState().moments, today), nestJoin: null };
	update((s) => ({ ...s, moments: next }));
	return next;
}

/** A nest joined or started here: its moment waits for the Nest tab. */
export function noteNestJoined(name: string, today = localDateKey(new Date())): void {
	update((s) => ({ ...s, moments: { ...s.moments, nestJoin: { name: name.slice(0, 80), on: today } } }));
}

/** The nest's moment was taken (its yes): it's done. */
export function clearNestJoin(): void {
	if (pushState().moments.nestJoin) update((s) => ({ ...s, moments: { ...s.moments, nestJoin: null } }));
}

/** A notification's link carries `source=push&k=<kind>`: taken out of the address on launch. */
export function stripPushParams(): void {
	try {
		const url = new URL(window.location.href);
		if (url.searchParams.get('source') !== 'push') return;
		url.searchParams.delete('source');
		url.searchParams.delete('k');
		const q = url.searchParams.toString();
		window.history.replaceState(window.history.state, '', `${url.pathname}${q ? `?${q}` : ''}${url.hash}`);
	} catch {
		// Harmless if it stays.
	}
}
